<template>
<div class="px-1 pt-1">
  <div>
    <span class="fs-4">{{ $t('page.info.form.title') }}</span>
    <RouterLink :to="{ name: 'info-device' }" class="btn btn-outline-secondary float-end">
      <font-awesome-icon :icon="['fas', 'mobile-screen']" />
      {{ $t('menu.info-device') }}
    </RouterLink>
  </div>
  <ScanInput ref="scanInput" :instruction="$t('action.scanBarcode')" @scanned="barcodeScanned" class="input-group" />

  <div v-if="error" class="alert alert-danger mt-2">
    {{ error }}
  </div>

  <div v-if="currentItem" :disabled="isLoading" class="pe-1">
    <h3 class="pt-4">
      <ItemField :item="currentItem" /> <UnitQuantity :quantity="currentStockQuantity" :estimated-quantity="currentEstimatedStockQuantity" :unit-name="currentItem?.baseUnitCode" />
    </h3>

    <div class="row g-3">

      <div class="col-sm-6 col-md-4 col-lg-3" v-if="!isQuantityValid">
        <label for="quantity" class="form-label">{{ $t('field.generic.quantity') }}</label>
        <div class="input-group">
          <input v-model="currentQuantity" type="number" class="form-control" id="quantity" ref="quantityInput" :class="{ 'is-invalid': !isQuantityValid }">
          <span class="input-group-text">{{ currentItem?.baseUnitCode }}</span>
        </div>
      </div>

      <div class="col-sm-6 col-md-4 col-lg-3" v-if="!isLotValid">
        <label for="lot" class="form-label"><input type="checkbox" :class="{ 'form-check-input': true, 'is-invalid': !isLotValid }" v-model="currentItem.trackLotNumber" disabled> {{ $t('field.masterdata.lot') }}</label>
        <Typeahead v-model="currentLot" id="lot" ref="lotInput" :inputClass="{ 'form-control': true, 'is-invalid': !isLotValid }" :items="currentItemStockLots" :minInputLength="1" :allowNew="true" :uppercase="true" />
      </div>

      <div class="col-sm-6 col-md-4 col-lg-3" v-if="!isBestBeforeValid">
        <label for="bestBefore" class="form-label"><input type="checkbox" :class="{ 'form-check-input': true, 'is-invalid': !isBestBeforeValid }" v-model="currentItem.trackBestBeforeDate" disabled> {{ $t('field.masterdata.bestBefore') }}</label>
        <input v-model="currentBestBefore" type="date" class="form-control" id="bestBefore" ref="bestBeforeInput" :class="{ 'is-invalid': !isBestBeforeValid }">
      </div>

      <div class="col-sm-6 col-md-4 col-lg-3" v-if="!isExpiryValid">
        <label for="expiry" class="form-label"><input type="checkbox" :class="{ 'form-check-input': true, 'is-invalid': !isExpiryValid }" v-model="currentItem.trackExpirationDate" disabled> {{ $t('field.masterdata.expiry') }}</label>
        <input v-model="currentExpiry" type="date" class="form-control" id="expiry" ref="expiryInput" :class="{ 'is-invalid': !isExpiryValid }">
      </div>

    </div>
  </div>

  <div v-if="currentItemBlock" class="pe-1">
    <div class="input-group pt-2">
      <RouterLink :to="{ name: 'itemblock', params: { itemBlockId: currentItemBlock.id } }" class="btn btn-outline-primary">
        <font-awesome-icon :icon="['fas', 'kit-medical']" />
        {{ currentItemBlock.code }}
      </RouterLink>
      <span class="input-group-text small text-melodi">{{ currentItemBlock.name }}</span>
    </div>
    <ItemBlockPartList :itemblock-id="currentItemBlock.id" />
  </div>

  <div v-if="currentDelivery" class="pe-1">
    <div class="input-group pt-2">
      <RouterLink :to="{ name: 'delivery', params: { deliveryId: currentDelivery.id } }" class="btn btn-outline-primary">
        <font-awesome-icon :icon="['fas', 'truck']" />
        {{ currentDelivery.code }}
      </RouterLink>
      <span class="input-group-text small text-melodi">{{ currentDelivery.description }}</span>
      <div class="input-group-text">
        <DeliveryStatus :status="currentDelivery.status" />
      </div>
    </div>
  </div>

  <div v-if="currentOrder" class="pe-1">
    <div class="input-group pt-2">
      <RouterLink :to="{ name: 'order', params: { orderId: currentOrder.id } }" class="btn btn-outline-primary">
        <font-awesome-icon :icon="['fas', 'cart-shopping']" />
        {{ currentOrder.code }}
      </RouterLink>
      <RouterLink v-if="currentOrder.status === ORDER_HEAD_STATUS.inProgress" :to="{ name: 'receiving', params: { orderId: currentOrder.id } }" class="btn btn-outline-primary">
        <font-awesome-icon :icon="['far', 'clipboard-list-check']" />
        {{ $t('menu.receivings') }}
      </RouterLink>
      <span class="input-group-text small text-melodi">{{ currentOrder.supplier?.name }}</span>
      <div class="input-group-text">
        <OrderStatus :status="currentOrder.status" />
      </div>
    </div>
  </div>

  <div v-if="userSettingsStore.data.developerMode" class="mt-2">
    <div v-if="rawScanType" class="alert mt-2 alert-light">
      <code>{{ rawScanType }}</code>
    </div>
    <div v-if="rawScanValue" class="alert mt-2 alert-light">
      <code>{{ rawScanValue }}</code>
    </div>
  </div>
</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { compareOptionalDate, compareOptionalString } from "@/services";
import { useItemsStore, useTradeItemsStore, useUserSettingsStore, useStockStore, useItemBlocksStore, useDeliveriesStore, useOrdersStore } from "@/stores";
import DeliveryStatus from '@/components/deliveries/DeliveryStatus.vue';
import OrderStatus from '@/components/orders/OrderStatus.vue';
import IGS1 from "@/models/IGS1";
import ScanInput from "@/components/form/ScanInput.vue";
import { ItemField } from '@/components/fields';
import UnitQuantity from "@/components/UnitQuantity.vue";
import ItemBlockPartList from "@/components/masterdata/ItemBlockPartList.vue";
import Stock from "@/models/frontend/Stock";
import Item from "@/models/frontend/Item";
import type IItemBlock from "@/models/responses/IItemBlock";
import type Delivery from "@/models/frontend/Delivery";
import type Order from "@/models/frontend/Order";
import { ORDER_HEAD_STATUS } from "@/models/Enums";

const i18n = useI18n();
const userSettingsStore = useUserSettingsStore();

const isLoading = ref(false);
const error = ref('');
const rawScanType = ref('');
const rawScanValue = ref('');

const currentItem = ref<Item | undefined>();
const currentItemBlock = ref<IItemBlock | undefined>();
const currentDelivery = ref<Delivery | undefined>();
const currentOrder = ref<Order | undefined>();
const currentQuantity = ref<number | undefined>();
const currentLot = ref<string | undefined>();
const currentBestBefore = ref<string | undefined>();
const currentExpiry = ref<string | undefined>();
const currentStockQuantity = ref<number>(0);
const currentEstimatedStockQuantity = ref<number>(0);
const currentItemStock = ref<Stock[]>();
const currentItemStockLots = ref<string[]>([]);

const scanInput = ref<InstanceType<typeof ScanInput> | null>(null);
const bestBeforeInput = ref<HTMLInputElement | null>(null);
const expiryInput = ref<HTMLInputElement | null>(null);
const lotInput = ref<HTMLInputElement | null>(null);
const quantityInput = ref<HTMLInputElement | null>(null);

const itemStore = useItemsStore();
const tradeItemStore = useTradeItemsStore();
const itemBlocksStore = useItemBlocksStore();
const deliveriesStore = useDeliveriesStore();
const ordersStore = useOrdersStore();

const isQuantityValid = computed(() => {
  if (!currentQuantity.value) { return false; }
  return currentQuantity.value > 0;
});

const isLotValid = computed(() => {
  if (!currentItem.value) { return false; }
  // return !currentItem.value.trackLotNumber || !!currentLot.value;
  if (currentItem.value.trackLotNumber && !currentLot.value) { return false; }
  if (!currentItem.value.trackLotNumber && !!currentLot.value) { return false; }
  return true;
});

const isBestBeforeValid = computed(() => {
  if (!currentItem.value) { return false; }
  if (currentItem.value.trackBestBeforeDate && !currentBestBefore.value) { return false; }
  if (!currentItem.value.trackBestBeforeDate && currentBestBefore.value) { return false; }
  return true;
});

const isExpiryValid = computed(() => {
  if (!currentItem.value) { return false; }
  if (currentItem.value.trackExpirationDate && !currentExpiry.value) { return false; }
  if (!currentItem.value.trackExpirationDate && currentExpiry.value) { return false; }
  return true;
});

const getDateString = (date: Date | undefined) => {
  if (!date) { return undefined; }
  const offset = date.getTimezoneOffset();
  date = new Date(date.getTime() - (offset * 60 * 1000));
  return date.toISOString().split('T')[0];
}

const barcodeScanned = async (barcode: string, gs1: IGS1 | undefined, bcType: string | undefined) => {
  currentItem.value = undefined;
  currentItemBlock.value = undefined;
  currentDelivery.value = undefined;
  currentOrder.value = undefined;
  currentQuantity.value = 1;
  currentLot.value = undefined;
  currentBestBefore.value = undefined;
  currentExpiry.value = undefined;
  let nextErrorField = '';

  const fncChar = String.fromCharCode(29); // the ASCII "group separator"
  rawScanType.value = bcType ?? '';
  rawScanValue.value = barcode.replace(RegExp(fncChar, "g"), '<GS>');

  // search item
  if (barcode || gs1) {
    const contentCode = gs1?.gtin ?? barcode;
    var tradeItem = tradeItemStore.getByCode(contentCode);
    if (tradeItem) {
      currentItem.value = itemStore.getById(tradeItem.itemId);
      currentQuantity.value = tradeItem.factor;
    }
    if (!currentItem.value && gs1?.itemCode) {
      currentItem.value = itemStore.getByCode(gs1.itemCode);
    }
  }

  // if not matched already, try get item by code
  if (!currentItem.value) {
    currentItem.value = itemStore.getByCode(barcode);
  }

  // if still not matched, check if it's an item block
  if (!currentItem.value) {
    const itemBlock = itemBlocksStore.getByCode(barcode);
    if (itemBlock) {
      // Display the item block parts
      currentItemBlock.value = itemBlock;
      error.value = '';
      scanInput.value?.focus();
      return;
    }
  }

  // if still not matched, check if it's a delivery
  if (!currentItem.value) {
    try {
      await deliveriesStore.fetchByCode(barcode);
      if (deliveriesStore.delivery) {
        currentDelivery.value = deliveriesStore.delivery;
        error.value = '';
        scanInput.value?.focus();
        return;
      }
    } catch (error) {
      // Ignore delivery lookup errors, continue with item lookup
      console.debug('Delivery lookup failed:', error);
    }
  }

  // if still not matched, check if it's an order
  if (!currentItem.value) {
    try {
      await ordersStore.fetchByCode(barcode);
      if (ordersStore.order) {
        currentOrder.value = ordersStore.order;
        error.value = '';
        scanInput.value?.focus();
        return;
      }
    } catch (error) {
      // Ignore order lookup errors, continue with item lookup
      console.debug('Order lookup failed:', error);
    }
  }

  if (!currentItem.value) {
    error.value = i18n.t('error.unknownItem') + ': "' + barcode + '"';
    return;
  }

  error.value = '';

  if (gs1?.quantity) {
    currentQuantity.value = gs1.quantity * currentQuantity.value;
  }

  if (gs1?.lot) {
    currentLot.value = gs1.lot;
  }
  if (!isLotValid.value && !nextErrorField) {
    nextErrorField = 'lot';
  }

  if (gs1?.bestBefore) {
    currentBestBefore.value = getDateString(gs1.bestBefore);
  }
  if (!isBestBeforeValid.value && !nextErrorField) {
    nextErrorField = 'bestBefore';
  }

  if (gs1?.expiry) {
    currentExpiry.value = getDateString(gs1.expiry);
  }
  if (!isExpiryValid.value && !nextErrorField) {
    nextErrorField = 'expiry';
  }

  await fetchCurrentItemStock();
  currentItemStockLots.value = [...new Set(currentItemStock.value?.map((stock) => stock.lot ?? '') ?? [])]; // set is used to remove duplicates
  currentStockQuantity.value = currentItemStock.value?.reduce((acc, stock) => acc + stock.quantity, 0) ?? 0;
  currentEstimatedStockQuantity.value = currentItemStock.value?.reduce((acc, stock) => acc + stock.estimatedQuantity, 0) ?? 0;

  scanInput.value?.focus();
};

const fetchCurrentItemStock = async () => {
  if (!currentItem.value) { return; }
  const stocksStore = useStockStore();
  const itemStock = await stocksStore.fetchAllByItem(currentItem.value.id);
  currentItemStock.value = itemStock.sort((a, b) =>
    a.item.name.localeCompare(b.item.name) ||
    compareOptionalDate(a.expiryAsDate, b.expiryAsDate) ||
    compareOptionalDate(a.bestBeforeAsDate, b.bestBeforeAsDate) ||
    compareOptionalString(a.lot, b.lot)
  );
}
</script>
